import { Camera } from '../camera';
import { Context } from '../context/Context';
import { FullscreenQuad } from './FullscreenQuad';
import { GBuffer } from './GBuffer';
import { HistoryBuffer } from './HistoryBuffer';
import { RenderTexture, createColorTexture, createFramebuffer } from './RenderTarget';

/**
 * Temporal anti-aliasing pass.
 *
 * Scene renders into `inputFbo` (RGBA16F + depth). After all opaque + transparent
 * passes complete, this pass blends the scene with the reprojected previous-frame
 * result (history) using motion vectors from the G-buffer. The blended result is
 * written to the next history slot AND copied to the downstream framebuffer
 * (bloom or canvas) via a passthrough.
 *
 * Coexists with FXAA — FXAA can run after TAA for residual edge softening.
 */
export class TaaPass {
  private inputFbo!: WebGLFramebuffer;
  private inputTexture!: RenderTexture;
  private inputDepthRenderbuffer!: WebGLRenderbuffer;
  private history!: HistoryBuffer;
  private width!: number;
  private height!: number;

  /** History weight; 0.9 = strong accumulation, 0.5 = light. */
  public blendAlpha = 0.1;

  init(ctx: Context): void {
    const gl = ctx.gl;
    this.width = ctx.viewPort.width;
    this.height = ctx.viewPort.height;

    this.inputTexture = new RenderTexture();
    this.inputTexture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.RGBA16F,
      gl.RGBA,
      gl.HALF_FLOAT,
    );
    this.inputFbo = createFramebuffer(gl, this.inputTexture.glTexture!);

    // Depth renderbuffer so opaque + transparent passes can write gl_FragDepth.
    this.inputDepthRenderbuffer = gl.createRenderbuffer()!;
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.inputDepthRenderbuffer);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, this.width, this.height);
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.inputFbo);
    gl.framebufferRenderbuffer(
      gl.FRAMEBUFFER,
      gl.DEPTH_ATTACHMENT,
      gl.RENDERBUFFER,
      this.inputDepthRenderbuffer,
    );
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    this.history = new HistoryBuffer(gl, this.width, this.height);
  }

  /** Scene FBO target — the chain renders here, then this pass consumes it. */
  getInputFramebuffer(): WebGLFramebuffer {
    return this.inputFbo;
  }

  /** Output of this frame's TAA blend, used as the input to bloom/fxaa/canvas. */
  getResultTexture(): RenderTexture {
    return this.history.current();
  }

  invalidateHistory(): void {
    // Caller sets camera.historyValid = false; nothing else to do here.
  }

  render(
    ctx: Context,
    quad: FullscreenQuad,
    gBuffer: GBuffer,
    camera: Camera,
    outputBuffer: WebGLFramebuffer | null,
  ): void {
    const gl = ctx.gl;

    // === 1. TAA blend → write to current history slot ===
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.history.currentFBO());
    gl.viewport(0, 0, this.width, this.height);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);

    const taaShader = ctx.engine.shaderManager.getTaaShader();
    ctx.setShader(taaShader);

    taaShader.uniformsContainer
      .setCurrentColor(this.inputTexture)
      .setHistoryColor(this.history.previous())
      .setMotionMap(gBuffer.motionTexture)
      .setTexelSize([1.0 / this.width, 1.0 / this.height])
      .setBlendAlpha(this.blendAlpha)
      .setHistoryValid(camera.historyValid ? 1 : 0);

    taaShader.validate();
    quad.draw(gl);

    // === 2. Passthrough current history → downstream FBO ===
    ctx.textureState.clear();
    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);

    const compositeShader = ctx.engine.shaderManager.getFullscreenCompositeShader();
    ctx.setShader(compositeShader);
    compositeShader.uniformsContainer.setTextureSampler(this.history.current());
    compositeShader.validate();
    quad.draw(gl);

    // === 3. Swap so this frame's result becomes next frame's "previous" ===
    this.history.swap();

    // After the first successful render the camera's history is valid; next
    // frame we'll blend instead of skipping.
    camera.historyValid = true;
  }

  destroy(gl: WebGL2RenderingContext): void {
    gl.deleteFramebuffer(this.inputFbo);
    gl.deleteRenderbuffer(this.inputDepthRenderbuffer);
    if (this.inputTexture.glTexture) gl.deleteTexture(this.inputTexture.glTexture);
    this.history.destroy(gl);
  }
}
